'use client';

import React, { useState } from 'react';
import cn from 'classnames';

import { responsiveBreakpointsPx } from '@/constants';
import Slider from '@/components/molecules/Slider/Slider';
import { LeagueGameEntry } from '@/services/contentful/contentTypes/UpcomingSports';
import { Text } from '@/ui-components/atoms/Text/Text';
import { GameCard } from '@/ui-components/molecules/GameCard/GameCard';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import {
    GameCardSliderFourOptions,
    GameCardSliderFourPlusOptions,
    GameCardSliderOneOption,
    GameCardSliderThreeOptions,
    GameCardSliderTwoOptions,
} from '@/components/molecules/Slider/SliderOptions';
import { ChevronIcon } from '@/assets/icons/ChevronIcon';
import type { Options as SplideOptions } from '@splidejs/splide';

import { deriveUpcomingSportsCards, hasGameCopyright } from './utils';
import styles from './gameCard.module.scss';

interface Slide {
    content: React.ReactNode;
    link?: Link;
}

interface Props {
    asset: SiteUpcomingSports;
    inMixtape?: boolean;
}

const prevArrowClassNames = cn('splide__arrow splide__arrow--prev', styles.leftArrow);
const nextArrowClassNames = cn('splide__arrow splide__arrow--next', styles.rightArrow);

const arrows = (aria: string) => (
    <div className={`splide__arrows ${styles.carouselArrows}`}>
        <button className={prevArrowClassNames} aria-label={`${aria} navigate previous arrow`}>
            <ChevronIcon />
        </button>
        <button className={nextArrowClassNames} aria-label={`${aria} navigate next arrow`}>
            <ChevronIcon />
        </button>
    </div>
);

const GameCards = ({ asset, inMixtape }: Props) => {
    const teamData = asset.fields.league_games.team;
    const nonTeamData = asset.fields.league_games.nonTeam;
    const teams = asset.fields.teams;
    const leagues = asset.fields.leagues;
    const [now] = useState(() => Date.now());

    const hasCopyright = hasGameCopyright(teamData, teams, leagues) || hasGameCopyright(nonTeamData, teams, leagues);

    const renderSportsCards = (data: LeagueGameEntry[]): Slide[] =>
        deriveUpcomingSportsCards(data, { teams, leagues, hasCopyright, now }).map((cardProps, index) => ({
            content: (
                <div key={index} className={styles.sportsCard}>
                    <GameCard {...cardProps} />
                </div>
            ),
        }));

    const teamCards = renderSportsCards(teamData);
    const nonTeamCards = renderSportsCards(nonTeamData);

    const getSlider = (options: React.ComponentProps<typeof Slider>['splideOptions'], cards: Slide[], aria: string) => {
        const paddingConsiderateOptions: SplideOptions = inMixtape
            ? {
                  ...options,
                  padding: '16px',
                  breakpoints: {
                      ...options.breakpoints,
                      [responsiveBreakpointsPx.sm]: {
                          ...(options.breakpoints?.[responsiveBreakpointsPx.sm] || {}),
                          padding: '16px',
                      },
                      [responsiveBreakpointsPx.md]: {
                          ...(options.breakpoints?.[responsiveBreakpointsPx.md] || {}),
                          padding: '24px',
                      },
                      [responsiveBreakpointsPx.lg]: {
                          ...(options.breakpoints?.[responsiveBreakpointsPx.lg] || {}),
                          padding: cards?.length === 2 ? '25%' : '0',
                      },
                  },
              }
            : options;

        return (
            <Slider
                slides={cards}
                isThemeDark={false}
                splideOptions={{ ...paddingConsiderateOptions, label: aria }}
                type="Default"
                customArrows={cards?.length > 4 ? arrows(aria) : undefined}
                className={styles.slider}
            />
        );
    };

    const getSliderByNumber = (length: number, cards: Slide[], aria: string) => {
        switch (length) {
            case 1:
                return getSlider(GameCardSliderOneOption, cards, aria);
            case 2:
                return getSlider(GameCardSliderTwoOptions, cards, aria);
            case 3:
                return getSlider(GameCardSliderThreeOptions, cards, aria);
            case 4:
                return getSlider(GameCardSliderFourOptions, cards, aria);
            default:
                return getSlider(GameCardSliderFourPlusOptions, cards, aria);
        }
    };

    const getWrapperClasses = (length: number) =>
        cn(styles.title, {
            [styles.centerTitle]: length < 4,
        });

    return (
        <Grid container>
            {teamCards?.length && teamCards.length > 0 ? (
                <Grid className={styles.wrapperFirstRow}>
                    {asset.fields.team_games_headine && (
                        <Text tag="h5" className={getWrapperClasses(teamCards.length)}>
                            {asset.fields.team_games_headine}
                        </Text>
                    )}
                    {getSliderByNumber(teamCards.length, teamCards, 'Team Sports Carousel')}
                </Grid>
            ) : undefined}

            {nonTeamCards?.length && nonTeamCards.length > 0 ? (
                <Grid className={styles.wrapperSecondRow}>
                    {asset.fields.nonteam_games_headline && (
                        <Text tag="h5" className={getWrapperClasses(nonTeamCards.length)}>
                            {asset.fields.nonteam_games_headline}
                        </Text>
                    )}
                    {getSliderByNumber(nonTeamCards.length, nonTeamCards, 'Event Carousel')}
                </Grid>
            ) : undefined}
        </Grid>
    );
};

export default GameCards;
